import React, { ReactNode, useEffect, useState } from "react";
import searchIcon from "media/icons/dls/search.svg";
import addIcon from "media/icons/dls/add.svg";
import "./SearchTechContentUser.scoped.scss";
import { useDispatch } from "react-redux";
import { getFilteredUsersList, setSearchLDAPManageTechContentUsersText, setSearchManageTechContentUsersText } from "store/userManagement/userManagementActions";
import useSelector from "store/useSelector";
import useDebounce from "utilities/useDebounce";
import BlockSpinner from "components/common/BlockSpinner";
import { ISW, SWTypes } from "interfaces/sw/SWInterfaces";
import { IManageUserUser, IAADUser } from "interfaces/user/UserInterfaces";

interface ISearchTechContentUserProps {
  setSelectedUserFunc(email: string): void,
  optional: boolean,
  isFilteredList: boolean,
  isLDAPSearch: boolean,
  title?: ReactNode,
  sw?: ISW,
}

const SearchTechContentUser: React.FC<ISearchTechContentUserProps> = ({ setSelectedUserFunc, optional, isFilteredList, isLDAPSearch, title, sw }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const [users, setUsers] = useState<IManageUserUser[]>([]);
  const [ldapUsers, setLDAPUsers] = useState<IAADUser[]>([]);
  const [isCurrentInstance, setIsCurrentInstance] = useState(false); //when more than one instance of this component exists, returns if the current one is in use  
  const [initialized, setInitialized] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 750);
  const manageUsersData = useSelector(store => store.userManagement.manageTechContentUsersData);
  const { getUsersOperation, userList, ldapUserList } = useSelector(store => store
    .userManagement
    .manageTechContentUsersData);
  const currentUser = useSelector(store => store.auth.currentUser.email);
  const dispatch = useDispatch();
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [showWarning, setShowWarning] = useState<React.ReactElement | undefined>();
  const [showWarningOptional, setShowWarningOptional] = useState<React.ReactElement | undefined>();

  useEffect(() => {
    setIsCurrentInstance(true);
  }, []);

  useEffect(() => {
    if (isLDAPSearch) {
      if (!debouncedSearchTerm) {
        setInitialized(false);
        setErrorMessage("");
        setLDAPUsers([]);
      } else {
        dispatch(setSearchLDAPManageTechContentUsersText(debouncedSearchTerm.trim()));
      }
    }
    else {
      if (isFilteredList) {
        dispatch(getFilteredUsersList({ searchTerm: debouncedSearchTerm.trim() }));
      }
      else {
        dispatch(setSearchManageTechContentUsersText(debouncedSearchTerm.trim()));
      }

      if (!initialized) {
        setInitialized(true);
      }
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (initialized) {
      if (isCurrentInstance) {
        setUsers(userList);
        setIsCurrentInstance(false);
      }
    }
  }, [userList]);

  useEffect(() => {
    if (isCurrentInstance) {
      setLDAPUsers(ldapUserList);
      setIsCurrentInstance(false);
      setInitialized(true);
    }
  }, [ldapUserList]);

  useEffect(() => {
    if (isCurrentInstance) {
      setErrorMessage(getUsersOperation?.errorMessage);
    }
  }, [getUsersOperation?.errorMessage]);

  let component: React.ReactElement | undefined;
  let titleArea = title || (<label className="tech-label"> Technical Content Reviewer {optional === true ? "(Optional)" : ""}{selectedUser}</label>);

  if (isCurrentInstance && getUsersOperation?.isWorking) {
    component = (
      <BlockSpinner />
    );
  } else if (errorMessage) {
    component = (
      <>
        {titleArea}
        <br />
        <label className="error">
          {errorMessage}
        </label>
      </>
    );
  } else if (!initialized) {
    component = (
      <>
        {titleArea}
        <br />
      </>
    );
  } else if (!ldapUsers.length && !users.length) {
    component = (
      <>
        {titleArea}
        <br />
        <label>No users found.</label>
      </>
    );
  } else {
    component = (
      <>
        {titleArea}
        {!isLDAPSearch && users.filter(x => x.email !== currentUser).map(r =>
          <div
            className="user"
            key={r.email}
          >
            <span>
              {r.name} ({r.email})
            </span>
            <img
              src={addIcon}
              className="add-button icon-medium hover-gray-bg"
              onClick={() => onUserSelect(r.email, r.name)}
              alt="Add"
            />
          </div>
        )}
        {isLDAPSearch && ldapUsers.filter(x => x.email !== currentUser).map(r =>
          <div
            className="user"
            key={r.email}
          >
            <span>
              {r.name} ({r.email})
            </span>
            <img
              src={addIcon}
              className="add-button icon-medium hover-gray-bg"
              onClick={() => onUserSelect(r.email, r.name)}
              alt="Add"
            />
          </div>
        )}
      </>
    );
  }

  const onUserSelect = (email: string, name: string) => {
    if ((sw?.tcc && sw?.dataClassification === "Confidential") && (sw?.type === SWTypes.MFGCL || sw?.type === SWTypes.MFGSWI || sw?.type === SWTypes.MFGRC)) {
      let warningMsg: React.ReactElement = <span>TCC sensitive content is about to be shared with the <b>{name}</b> TechContentApprover.</span>;
      if (isLDAPSearch) {
        setShowWarning(warningMsg);
      }
      else {
        setShowWarningOptional(warningMsg);
      }
    }
    setSelectedUserFunc(email);
    setSelectedUser("(" + name + ")");
  }

  const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowWarning(undefined);
    setShowWarningOptional(undefined);
    setSearchTerm(e.target.value);
    setIsCurrentInstance(true);
  }

  return (
    <>
      <br />
      <div className="search-user-bar">
        <div className="input-holder">
          <input
            type="text"
            id="search-user"
            placeholder="Search by name or email..."
            disabled={isCurrentInstance && manageUsersData.getUsersOperation?.isWorking}
            value={searchTerm}
            onChange={onSearchTermChange}
          />
          <img
            src={searchIcon}
            alt="Search"
            className="icon-small"
          />
        </div>
        <span className="msg">
          {showWarning}
          {showWarningOptional}
        </span>

      </div>
      <div className="results">
        {component}
      </div>
    </>
  );
}

export default SearchTechContentUser;